import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { Checkbox, Typography, Row, Spin, Card, Button, Divider, message } from 'antd';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { setWarrantyData } from '../../../redux/warranty/actions';
import { useVerifyNumber } from '../registration-line/hooks/useVerifyNumber';
import { setTitle } from '../../../redux/title/action';
import { StyledContainer } from '../registration-line/styles';
import { RenderForm } from '../../../components/forms';
import { FOMR_INPUT_REGISTER, FORM_UPLOAD_PACKAGE } from './forms';
import { ContainerButton } from '../../../styles/global-style';
import { ButtonTheme } from '../registration-line/components/buttons';
import {
  channelBuyQuery,
  storeTypeCodeQuery,
} from '../../../services/react-query/optionMasterQuery';
import { useGetProduct } from './hooks/useGetProduct';
import { ROUTES_PATH } from '../../../resources/routes-name';
import { CustomImage } from '../../../functions/Image';
import { color } from '../../../resources';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useInsertWarranty } from './hooks/useInsertWarranty';
import liff from '@line/liff';

const termsStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '16px',
  flexDirection: 'column',
  gap: '16px',
};

const cardContainerStyle = {
  display: 'flex',
  overflowX: 'auto',
  gap: '16px',
  padding: '8px 4px',
  width: '100%',
  '::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
};

const selectedCardContainerStyle = {
  ...cardContainerStyle,
  justifyContent: 'center',
  overflowX: 'hidden',
};

const cardStyle = {
  width: '160px',
  flex: '0 0 auto',
  marginBottom: '16px',
  backgroundColor: color?.bg_gray,
};

const selectedCardStyle = {
  ...cardStyle,
  border: `2px solid ${color?.blueFocus}`,
};

const navigationButtonStyle = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 1,
  opacity: 0.8,
};

const cardContainerWrapperStyle = {
  position: 'relative',
  width: '100%',
};

export const RegisterInfo = () => {
  const [loading, setLoading] = useState(false);
  const [channelBuyType, setChannelBuyType] = useState(null);
  const { warrantyNumberCode } = useLocation().state;
  const { mutate: onCallTele } = useVerifyNumber({ setLoading });
  const dispatch = useDispatch();
  const { control, setValue, watch, getValues, handleSubmit } = useForm();
  const [isAccepted, setIsAccepted] = useState(false);
  const { warranty, items } = useSelector((state) => state.warranty) || {};
  const userProfile = useSelector((state) => state.info);
  const history = useHistory();
  const channelBuyId = watch('channelBuyId');
  const mobileModelId = watch('mobileModelId');
  const containerRef = useRef(null);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { data: channelBuyList, isLoading: loadingChannelBuy } = channelBuyQuery();
  const { mutate: onInsertWarranty, isPending: loadingInsertWarranty } = useInsertWarranty();
  const { data: storeTypeCodeList, isLoading: loadingStoreTypeCode } =
    storeTypeCodeQuery(channelBuyType);

  useEffect(() => {
    const selectedChannelBuy = channelBuyList?.find((el) => el?.value === channelBuyId);
    if (selectedChannelBuy && selectedChannelBuy?.channelBuyType === 'COM7') {
      setChannelBuyType('COM7');
    } else {
      setChannelBuyType('');
    }
  }, [channelBuyId, channelBuyList]);

  useEffect(() => {
    if (warrantyNumberCode) {
      setLoading(true);
      onCallTele(
        { warrantyNumberCode },
        {
          onSuccess: (data) => {
            dispatch(setWarrantyData(data));
          },
        },
      );
    }
  }, [warrantyNumberCode]);

  useEffect(() => {
    dispatch(setTitle('ลงทะเบียนรับประกันสินค้า'));
  }, [dispatch]);

  const { mobileBrandOptions, mobileModelOptions, productOptions } = useGetProduct({
    watch,
    items,
  });

  useEffect(() => {
    if (warranty) {
      setValue('warrantyNumberCode', warranty?.warrantyNumberCode);
    }
    if (userProfile) {
      setValue('fullName', userProfile?.fullName);
      setValue('telephone', userProfile?.telephone);
    }

    if (channelBuyList?.length > 0) {
      setValue('channelBuyId', channelBuyList[0]?.value);
    }

    if (storeTypeCodeList?.length > 0) {
      setValue('storeId', storeTypeCodeList[0]?.value);
    } else {
      setValue('storeId', '99');
    }

    if (mobileBrandOptions?.length > 0) {
      setValue('mobileBrandId', mobileBrandOptions[0]?.value);
    }

    if (mobileModelOptions?.length > 0) {
      setValue('mobileModelId', mobileModelOptions[0]?.value);
    } else {
      setValue('mobileModelId', '99');
    }
  }, [
    warranty,
    userProfile,
    channelBuyList,
    storeTypeCodeList,
    mobileBrandOptions,
    mobileModelOptions,
  ]);

  useEffect(() => {
    if (mobileModelId && productOptions?.length > 0 && containerRef?.current) {
      handleScroll();
    } else {
      setShowLeftButton(false);
      setShowRightButton(false);
    }
  }, [mobileModelId, productOptions]);

  const handleCheckboxChange = (e) => {
    if (!selectedProduct) {
      return;
    }
    setIsAccepted(e.target.checked);
  };

  const onSubmit = (value) => {
    const submitData = {
      ...value,
      mapProductId: selectedProduct?.value,
    };
    onInsertWarranty(submitData);
  };

  const handleChange = useCallback((_, item) => {
    switch (item.name) {
      case 'channelBuyId':
        setValue('mobileBrandId', '');
        setValue('mobileModelId', '');
        setValue('selectedProductId', '');
        setValue('storeId', ''); // Reset store selection when channel changes
        break;
      case 'mobileBrandId':
        setValue('mobileModelId', '');
        setValue('selectedProductId', '');
        break;
      case 'mobileModelId':
      default:
        break;
    }
  }, []);

  const handlePolicyClick = () => {
    try {
      if (typeof liff === 'undefined' || !liff.isLoggedIn()) {
        window.open(ROUTES_PATH.ROUTE_TERM_WARRANTY.PATH, '_blank');
        return;
      }

      liff.openWindow({
        url: ROUTES_PATH.ROUTE_TERM_WARRANTY.PATH,
        external: true,
      });
    } catch (error) {
      message.error(error);
      window.open(ROUTES_PATH.ROUTE_TERM_WARRANTY.PATH, '_blank');
    }
  };

  const handlePolicyTouch = (e) => {
    e.preventDefault();
    handlePolicyClick();
  };

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setShowLeftButton(scrollLeft > 0);
      setShowRightButton(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (containerRef.current) {
      const scrollAmount = 300;
      containerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setValue('selectedProductId', product.value);
  };

  const handleCancelProduct = () => {
    setSelectedProduct(null);
    setIsAccepted(false);
    setValue('selectedProductId', null);
  };

  return (
    <Spin spinning={loadingChannelBuy || loadingStoreTypeCode || loading}>
      <StyledContainer style={{ paddingBottom: '10px', maxWidth: '100vw' }}>
        <RenderForm
          control={control}
          setValue={setValue}
          getValues={getValues}
          forms={FOMR_INPUT_REGISTER({
            channelBuyType,
            channelBuyList,
            mobileBrandOptions,
            mobileModelOptions,
            selectedProduct,
            storeTypeCodeList,
          })}
          onChange={handleChange}
        />
        <div style={termsStyle}>
          <Row justify="end">
            <Typography.Text>รุ่นฟิล์มและกระจกกันรอย</Typography.Text>
          </Row>
          <div style={cardContainerWrapperStyle}>
            {mobileModelId && showLeftButton && (
              <Button
                icon={<LeftOutlined />}
                onClick={() => scroll('left')}
                style={{ ...navigationButtonStyle, left: 0 }}
              />
            )}
            {mobileModelId && showRightButton && !selectedProduct && (
              <Button
                icon={<RightOutlined />}
                onClick={() => scroll('right')}
                style={{ ...navigationButtonStyle, right: 0 }}
              />
            )}
            <div
              ref={containerRef}
              style={selectedProduct ? selectedCardContainerStyle : cardContainerStyle}
              onScroll={handleScroll}
            >
              {(selectedProduct ? [selectedProduct] : productOptions).map((product, index) => (
                <Card
                  key={selectedProduct ? product.value : index}
                  style={product.value === selectedProduct?.value ? selectedCardStyle : cardStyle}
                >
                  <CustomImage
                    key={`img-${product.value}`}
                    src={product.productImageUrl}
                    alt={product.productFullName}
                    style={{ width: '100%', height: 'auto' }}
                    preview
                  />
                  <Typography.Text style={{ fontSize: '10px' }}>
                    ชื่อสินค้า: {product.productFullName}
                  </Typography.Text>
                  {!selectedProduct && (
                    <ButtonTheme
                      useFor="SELECT_PRODUCT"
                      onClick={() => handleSelectProduct(product)}
                      style={{ width: '100%', marginTop: '8px' }}
                    />
                  )}
                  {selectedProduct?.value === product.value && (
                    <ButtonTheme
                      useFor="CANCEL_SELECT_PRODUCT"
                      onClick={handleCancelProduct}
                      style={{ width: '100%', marginTop: '8px' }}
                    />
                  )}
                </Card>
              ))}
            </div>
          </div>
          <Divider style={{ marginTop: '-10px' }} />
          <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
            <RenderForm
              control={control}
              setValue={setValue}
              getValues={getValues}
              forms={FORM_UPLOAD_PACKAGE()}
            />
          </div>
          <Checkbox
            checked={isAccepted}
            onChange={handleCheckboxChange}
            disabled={!selectedProduct}
          >
            <span style={{ color: !selectedProduct ? '#999' : 'inherit' }}>
              ฉันได้ยอมรับ
              {!selectedProduct && (
                <Typography.Text type="danger" style={{ marginLeft: '8px' }}>
                  (กรุณาเลือกสินค้าก่อน)
                </Typography.Text>
              )}
            </span>
          </Checkbox>
          <Row justify="end">
            <Button
              type="link"
              onClick={handlePolicyClick}
              onTouchEnd={handlePolicyTouch}
              style={{
                padding: 0,
                height: 'auto',
                color: '#1890ff',
                textDecoration: 'underline',
                cursor: 'pointer',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
              }}
            >
              ข้อตกลงการรับประกันสินค้า
            </Button>
          </Row>
          <ContainerButton center style={{ marginBottom: '13px' }}>
            {loadingInsertWarranty ? (
              <Spin spinning={loadingInsertWarranty} />
            ) : (
              <ButtonTheme
                useFor="REGISTER_WARRANTY"
                disabled={!isAccepted || !selectedProduct}
                onClick={handleSubmit(onSubmit)}
                style={{ marginBottom: '100px' }}
              />
            )}
          </ContainerButton>
        </div>
      </StyledContainer>
    </Spin>
  );
};
